/**
 * Треки фоновой музыки (см. BackgroundMusic): свой у меню и свой у партии.
 *
 * volume — громкость трека при ползунке музыки на максимуме. Она у треков
 * своя и подбирается на слух. Для справки: трек меню записан тише темы партии
 * примерно на 2 дБ (интегральная громкость −17,0 против −15,1 LUFS, замер
 * ffmpeg ebur128) — при равных volume он звучал бы тише.
 *
 * Трек меню в игре — menu_theme.mp3: пережатая копия (3,0 МБ вместо 6,3 МБ).
 * Оригинал лежит рядом, Strategikon_Music_Menu.mp3 (320 кбит/с), и в игре не
 * используется. Заменив оригинал, копию пересобрать из него:
 *   ffmpeg -i Strategikon_Music_Menu.mp3 -map 0:a:0 -map_metadata -1 \
 *     -c:a libmp3lame -q:a 4 menu_theme.mp3
 * (переменный битрейт V4, в среднем ~150 кбит/с) и перемерить громкость.
 */
export const BACKGROUND_MUSIC_TRACKS = Object.freeze({
  // Все экраны вне партии: главное меню, профиль, магазин, лобби
  MENU: Object.freeze({src: '/sounds/menu_theme.mp3', volume: 0.125}),
  // Тема партии: в партии (сетевой и локальный матч, обучение, миссия,
  // просмотр записи) звучит между треками блоков раундов, тише их. Подобрана
  // на слух: тема плотнее треков блоков, и замер громкости ее недооценивает
  // (при 0.085 она звучала громче трека последних раундов, затем 0.063,
  // 0.044 — на 30% тише, теперь еще на 15% тише)
  GAME: Object.freeze({src: '/sounds/main_theme.mp3', volume: 0.037}),
});

// Оригинал трека меню, из которого пережат menu_theme.mp3. Хранится в
// репозитории рядом с копией; игра его не загружает
export const MENU_MUSIC_ORIGINAL_SRC = '/sounds/Strategikon_Music_Menu.mp3';

/**
 * Треки блоков раундов партии (см. MATCH_MUSIC_ROUND_BLOCKS).
 *
 * Громкость — база трека × MATCH_MUSIC_VOLUME_FACTOR. База считается от
 * уровня музыки меню. Исходно ее подобрали по замеру: при базе 0.212
 * (first_rounds_1), 0.125 (menu_theme), 0.098 (last_rounds_1) и 0.094
 * (last_rounds_2) трек звучит так же громко, как трек меню в меню (volume
 * меню × разница интегральной громкости, ffmpeg ebur128: menu_theme
 * −17,0 LUFS, first_rounds_1 −21,6, last_rounds_1 −14,9, last_rounds_2
 * −14,5). Дальше — на слух: у трека меню и треков последних раундов база
 * 0.11, чуть тише уровня меню. Тема партии между ними тише (см.
 * BACKGROUND_MUSIC_TRACKS.GAME). Прежние, в разы меньшие громкости
 * (0.063 / 0.037 / 0.029 при теме 0.03) на слух терялись за звуками поля.
 *
 * Скорость — playbackRate (по умолчанию 1, исходная): 0.8 — трек играет на
 * 20% медленнее и на столько же дольше. Браузер замедляет его сам, на лету
 * (HTMLMediaElement.playbackRate), файл тот же. Высота тона при этом
 * сохраняется; preservesPitch: false — не сохранять, звук как у замедленной
 * пленки (при 0.8 — ниже почти на 4 полутона). Разумные пределы — 0.5…2: за
 * ними одни браузеры глушат звук, другие портят его.
 *
 * Файлы в public/sounds/music — пережатые копии оригиналов (wav, mp3
 * 320 кбит/с), как и трек меню; оригиналы игре не нужны. Новый трек
 * пережимать так же и громкость его мерить так же:
 *   ffmpeg -i <оригинал> -map 0:a:0 -map_metadata -1 -c:a libmp3lame -q:a 4 <копия>.mp3
 *   ffmpeg -i <копия>.mp3 -af ebur128 -f null -
 */
// Множитель громкости треков блоков в партии относительно музыки меню:
// 1 — вровень с меню, 0.8 — на 20% тише, 0.6 — на 40% тише
export const MATCH_MUSIC_VOLUME_FACTOR = 0.8;

export const MATCH_MUSIC_TRACKS = Object.freeze({
  FIRST_ROUNDS_1: Object.freeze({
    src: '/sounds/music/first_rounds_1.mp3',
    volume: 0.212 * MATCH_MUSIC_VOLUME_FACTOR,
  }),
  // Тот же файл, что звучит в меню; громкость своя и подстраивается отдельно
  // от меню (база 0.11 против 0.125 в меню, и еще × множитель партии)
  MENU_THEME: Object.freeze({
    src: '/sounds/menu_theme.mp3',
    volume: 0.11 * MATCH_MUSIC_VOLUME_FACTOR,
  }),
  LAST_ROUNDS_1: Object.freeze({
    src: '/sounds/music/last_rounds_1.mp3',
    volume: 0.11 * MATCH_MUSIC_VOLUME_FACTOR,
    playbackRate: 1,
  }),
  // Оригинал — «strategikon trailer music.wav»
  LAST_ROUNDS_2: Object.freeze({
    src: '/sounds/music/last_rounds_2.mp3',
    volume: 0.11 * MATCH_MUSIC_VOLUME_FACTOR,
    playbackRate: 1,
  }),
});

// Свои треки каждого блока раундов: новый трек дописывается в перечень того
// блока, для которого он написан. Звучат они и в следующем блоке (см.
// MATCH_MUSIC_ROUND_BLOCKS)
export const FIRST_ROUNDS_MUSIC_TRACKS = Object.freeze([
  MATCH_MUSIC_TRACKS.FIRST_ROUNDS_1,
]);
export const MIDDLE_ROUNDS_MUSIC_TRACKS = Object.freeze([
  MATCH_MUSIC_TRACKS.MENU_THEME,
]);
export const LAST_ROUNDS_MUSIC_TRACKS = Object.freeze([
  MATCH_MUSIC_TRACKS.LAST_ROUNDS_1,
  MATCH_MUSIC_TRACKS.LAST_ROUNDS_2,
]);

/**
 * Музыка партии по блокам раундов. Трек блока и тема партии чередуются: трек
 * блока → тема (MATCH_MUSIC_THEME_REPEATS раз подряд) → трек блока → тема…
 * Трек блока каждый раз выбирается случайно, с равной вероятностью, из его
 * tracks (тот же трек дважды подряд не звучит, если есть другие).
 *
 * В tracks блока — его собственные треки и треки предыдущего блока: в
 * средних раундах звучат и треки первых, в последних — и треки средних
 * (только собственные треки средних, без первых). Новый трек достаточно
 * дописать в перечень *_ROUNDS_MUSIC_TRACKS выше.
 *
 * Блок действует с раунда fromRound до начала следующего блока; последний — до
 * конца партии, сколько бы в ней ни было раундов. Расстановка (раунд 0) — в
 * первом блоке. Начался новый блок — звучащий трек (или очередной повтор
 * темы) доигрывает, и следующим звучит трек нового блока.
 */
export const MATCH_MUSIC_ROUND_BLOCKS = Object.freeze([
  Object.freeze({
    id: 'first_rounds',
    fromRound: 1,
    tracks: FIRST_ROUNDS_MUSIC_TRACKS,
  }),
  Object.freeze({
    id: 'middle_rounds',
    fromRound: 6,
    tracks: Object.freeze([...MIDDLE_ROUNDS_MUSIC_TRACKS, ...FIRST_ROUNDS_MUSIC_TRACKS]),
  }),
  Object.freeze({
    id: 'last_rounds',
    fromRound: 11,
    tracks: Object.freeze([...LAST_ROUNDS_MUSIC_TRACKS, ...MIDDLE_ROUNDS_MUSIC_TRACKS]),
  }),
]);

// В обучении блоки раундов не действуют: во всех главах свой перечень
export const TUTORIAL_MUSIC_BLOCK = Object.freeze({
  id: 'tutorial',
  tracks: Object.freeze([MATCH_MUSIC_TRACKS.FIRST_ROUNDS_1]),
});

// Сколько раз подряд звучит тема партии между треками блоков. Повторы идут по
// кругу без паузы — тема гаснет к концу и сразу проявляется с начала; пауза
// MATCH_MUSIC_GAP_MS — только перед темой и после последнего повтора.
// 0 — темы нет, треки блока идут друг за другом
export const MATCH_MUSIC_THEME_REPEATS = 3;

// Тишина между треками партии: трек доиграл (последние секунды он гаснет) —
// пауза — следующий трек. Чтобы смена трека не звучала обрывом, но и музыка не
// пропадала надолго
export const MATCH_MUSIC_GAP_MS = 3000;

// Молчит ли музыка партии на своем ходу (и на своей части одновременного
// снабжения сетевой партии), см. checkIfBackgroundMusicPlaying. До 2026-10-26
// было true; выключено по просьбе автора: в партии против ботов их ходы
// короткие, и музыка лишь на секунды проявлялась между своими ходами. При
// false музыка в партии молчит только в расстановке стартовых кварталов.
// true — вернуть прежнее правило
export const MATCH_MUSIC_SILENT_ON_OWN_TURN = false;
